import { NetworkImage, PlaceholderImage, Skeleton, Typography } from '@/components/atoms';
import { ChevronLeftIcon, ChevronRightButtonIcon } from '@/components/atoms/icons';
import { useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { isSmallScreen } from '@/core/utils/responsive';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { ImageSourcePropType, NativeScrollEvent, NativeSyntheticEvent, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';

/** Web-only: Figma/detail gallery pagination (native keeps 6px + bottom: 8). */
const WEB_DOTS_BOTTOM_OFFSET = 12;
const WEB_DOT_SIZE_DESKTOP = 12;
/** Mobile web — smaller white dots so they don’t dominate the image. */
const WEB_DOT_SIZE_MOBILE = 8;
const IS_WEB = Platform.OS === 'web';
/** Default auto-advance. Callers pass `disableAutoScroll` to opt out. */
const WEB_CAROUSEL_AUTO_SCROLL_MS = 3000;
const SEEDED_AUTO_SCROLL_MIN_MS = 3000;
const SEEDED_AUTO_SCROLL_SPREAD_MS = 1500;
const SEEDED_AUTO_SCROLL_FIRST_MIN_MS = 1500;

/**
 * Signed 32-bit truncation. Matches the previous bitwise cast so auto-scroll
 * seeds stay on the same delay, and uses `Math.trunc` (Sonar S7767).
 */
function toInt32(value: number): number {
    const truncated = Math.trunc(value);
    const wrapped = truncated % 4294967296;
    const unsigned = wrapped < 0 ? wrapped + 4294967296 : wrapped;
    if (unsigned >= 2147483648) {
        return unsigned - 4294967296;
    }
    return unsigned;
}

function stableSeedHash(seed: string): number {
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
        hash = toInt32(hash * 31 + (seed.codePointAt(i) ?? 0));
    }
    return Math.abs(hash);
}

interface AutoScrollTiming {
    intervalMs: number;
    firstDelayMs: number;
}

function resolveAutoScrollTiming(baseMs: number, seed: string | undefined): AutoScrollTiming {
    if (!seed) {
        return { intervalMs: baseMs, firstDelayMs: baseMs };
    }
    const intervalMs = SEEDED_AUTO_SCROLL_MIN_MS + (stableSeedHash(seed) % (SEEDED_AUTO_SCROLL_SPREAD_MS + 1));
    // Seeded start offset, so restarts (e.g. after page scroll) stay out of step.
    const firstDelayMs = SEEDED_AUTO_SCROLL_FIRST_MIN_MS + (stableSeedHash(`${seed}:phase`) % intervalMs);
    return { intervalMs, firstDelayMs };
}

function getCarouselImageKey(src: ImageSourcePropType, index: number): string {
    if (typeof src === 'number') {
        return `local-${src}`;
    }
    if (typeof src === 'object' && src !== null && 'uri' in src && typeof src.uri === 'string' && src.uri) {
        return src.uri;
    }
    return `slide-${index}`;
}

function getNetworkImageUri(src: ImageSourcePropType): string | null {
    if (typeof src === 'object' && src !== null && 'uri' in src) {
        return src.uri ?? null;
    }
    return null;
}

/** Mirrors NetworkImage: anything that isn't an http(s) URL renders its placeholder. */
function isLoadableImage(src: ImageSourcePropType): boolean {
    if (typeof src === 'number') {
        return true;
    }
    const uri = getNetworkImageUri(src)?.trim() ?? '';
    return uri.startsWith('http://') || uri.startsWith('https://');
}

function getMultiSlideGradientColors(): [string, string] {
    if (IS_WEB) {
        return ['transparent', 'rgba(0, 0, 0, 0.35)'];
    }
    return ['transparent', 'rgba(0, 0, 0, 0.5)'];
}

export interface ImageCarouselProps {
    images?: ImageSourcePropType[];
    height?: number;
    constructionCompleted?: number; // Construction completion percentage (0-100)
    onScrollStart?: () => void;
    onScrollEnd?: () => void;
    onPress?: () => void; // Optional press handler for navigation
    /** Fires when the user changes slides (swipe, arrow, or dot). Auto-advance does not call this. */
    onManualIndexChange?: (index: number) => void;
    /** Show left/right slide buttons (web only). Used e.g. on property detail page. */
    showNavigationButtons?: boolean;
    /** Color of the active pagination dot. Defaults to theme button color. */
    activeDotColor?: string;
    imageTransition?: number;
    /**
     * Auto-advance is on by default (every 3s) on web and mobile.
     * Pass true to keep the carousel manual.
     * Fewer than two images never starts a timer.
     */
    disableAutoScroll?: boolean;
    /**
     * Suspend the auto-advance timer without tearing down `disableAutoScroll`'s
     * "manual forever" setting — pass `true` while a surrounding screen's own
     * ScrollView is actively being dragged. The timer's tick both scrolls and
     * calls `setState`, competing with the page's own active scroll gesture;
     * pausing here avoids that without giving up auto-advance during idle time.
     */
    autoScrollPaused?: boolean;
    /**
     * Stable per-card seed (e.g. project id). When set, auto-advance uses a
     * seed-derived 3–4.5s interval and start offset so sibling cards in a grid
     * don't all switch slides together. Omit to keep the fixed 3s cadence.
     */
    autoScrollSeed?: string;
    /**
     * Web pagination style:
     * - `gallery` (default): property details — 12px desktop / 8px mobile, inactive 0.67
     * - `listing`: properties-web ListingCard — always 8px, inactive 0.5
     * Native dots are unchanged.
     */
    paginationVariant?: 'gallery' | 'listing';
    /**
     * Web only: the image list is still being resolved. Shows one shimmer instead
     * of the placeholder, so the placeholder only appears for no / failed images.
     */
    loading?: boolean;
}

function ConstructionCompletedOverlay({
    constructionCompleted,
}: Readonly<{ constructionCompleted: number }>) {
    const { t } = useTranslation();
    if (constructionCompleted <= 0) {
        return null;
    }
    return (
        <View style={styles.completionStatusOverlay}>
            <Typography variant="caption" color="primary">
                {t('properties.constructionCompleted')}: {constructionCompleted}%
            </Typography>
        </View>
    );
}

function CarouselPaginationDots({
    slideImages,
    activeIndex,
    isRTL,
    webDotSize,
    webDotInactiveStyle,
    activeDotColor,
    onDotPress,
}: Readonly<{
    slideImages: ImageSourcePropType[];
    activeIndex: number;
    isRTL: boolean;
    webDotSize: number;
    webDotInactiveStyle: object;
    activeDotColor?: string | undefined;
    onDotPress: (index: number) => void;
}>) {
    const c = useUIColors();

    return (
        <View
            style={[
                styles.dotsContainer,
                isRTL && styles.dotsContainerRTL,
                IS_WEB ? styles.dotsContainerWeb : styles.dotsContainerNative,
            ]}
            pointerEvents="box-none"
        >
            {slideImages.map((src, i) => {
                const isActive = i === activeIndex;
                const dotKey = `dot-${getCarouselImageKey(src, i)}`;
                if (IS_WEB) {
                    return (
                        <Pressable
                            key={dotKey}
                            onPress={(event) => {
                                event.stopPropagation();
                                onDotPress(i);
                            }}
                            style={[
                                {
                                    width: webDotSize,
                                    height: webDotSize,
                                    borderRadius: webDotSize / 2,
                                },
                                isActive ? styles.dotWebActive : webDotInactiveStyle,
                            ]}
                        />
                    );
                }
                return (
                    <View
                        key={dotKey}
                        style={[
                            styles.dot,
                            {
                                backgroundColor: isActive
                                    ? (activeDotColor ?? c.buttonDefault)
                                    : c.buttonDisabled,
                            },
                        ]}
                    />
                );
            })}
        </View>
    );
}

function resolvePrevChevronIcon(isRTL: boolean, color: string) {
    if (isRTL) {
        return <ChevronRightButtonIcon size={32} color={color} />;
    }
    return <ChevronLeftIcon size={32} color={color} />;
}

function resolveNextChevronIcon(isRTL: boolean, color: string) {
    if (isRTL) {
        return <ChevronLeftIcon size={32} color={color} />;
    }
    return <ChevronRightButtonIcon size={32} color={color} />;
}

function CarouselWebNavChevrons({
    isRTL,
    activeIndex,
    onGoToSlide,
}: Readonly<{
    isRTL: boolean;
    activeIndex: number;
    onGoToSlide: (index: number) => void;
}>) {
    const c = useUIColors();
    const { t } = useTranslation();
    const prevStyle = isRTL ? styles.chevronRight : styles.chevronLeft;
    const nextStyle = isRTL ? styles.chevronLeft : styles.chevronRight;

    return (
        <>
            <Pressable
                style={[styles.chevronButton, prevStyle]}
                onPress={() => onGoToSlide(activeIndex - 1)}
                accessibilityRole="button"
                accessibilityLabel={t('accessibility.carouselPrevious')}
            >
                {resolvePrevChevronIcon(isRTL, c.fontPrimary)}
            </Pressable>
            <Pressable
                style={[styles.chevronButton, nextStyle]}
                onPress={() => onGoToSlide(activeIndex + 1)}
                accessibilityRole="button"
                accessibilityLabel={t('accessibility.carouselNext')}
            >
                {resolveNextChevronIcon(isRTL, c.fontPrimary)}
            </Pressable>
        </>
    );
}

function MultiSlideCarousel({
    slideImages,
    carouselWidth,
    activeIndex,
    isRTL,
    webDotSize,
    webDotInactiveStyle,
    activeDotColor,
    imageTransition,
    showNavigationButtons,
    constructionCompleted,
    scrollRef,
    onScroll,
    onScrollBeginDrag,
    onScrollEndDrag,
    onImagePress,
    onGoToSlide,
    onImageError,
}: Readonly<{
    slideImages: ImageSourcePropType[];
    carouselWidth: number;
    activeIndex: number;
    isRTL: boolean;
    webDotSize: number;
    webDotInactiveStyle: object;
    activeDotColor?: string | undefined;
    imageTransition?: number | undefined;
    showNavigationButtons: boolean;
    constructionCompleted?: number | undefined;
    scrollRef: React.RefObject<ScrollView | null>;
    onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
    onScrollBeginDrag: () => void;
    onScrollEndDrag: () => void;
    onImagePress: () => void;
    onGoToSlide: (index: number) => void;
    onImageError: (imageKey: string) => void;
}>) {
    return (
        <>
            <ScrollView
                ref={scrollRef}
                horizontal
                pagingEnabled
                showsHorizontalScrollIndicator={false}
                onScroll={onScroll}
                scrollEventThrottle={16}
                onScrollBeginDrag={onScrollBeginDrag}
                onScrollEndDrag={onScrollEndDrag}
                onMomentumScrollEnd={onScrollEndDrag}
                nestedScrollEnabled={true}
            >
                {slideImages.map((src, idx) => (
                    <Pressable
                        key={getCarouselImageKey(src, idx)}
                        style={[styles.slide, carouselWidth ? { width: carouselWidth } : null]}
                        onPress={onImagePress}
                    >
                        <NetworkImage
                            url={getNetworkImageUri(src)}
                            style={styles.image}
                            resizeMode="cover"
                            {...(IS_WEB && { onError: () => onImageError(getCarouselImageKey(src, idx)) })}
                            {...(imageTransition !== undefined && { transition: imageTransition })}
                        />
                    </Pressable>
                ))}
            </ScrollView>
            {/* Grey wash removed — the dark dotsGradient below provides sufficient
                 contrast for the pagination dots without creating a white shadow. */}
            {/* Gradient below dots for better visibility */}
            <LinearGradient
                pointerEvents="none"
                colors={getMultiSlideGradientColors()}
                locations={[0, 1]}
                style={[styles.dotsGradient, IS_WEB && styles.dotsGradientWeb]}
            />
            <CarouselPaginationDots
                slideImages={slideImages}
                activeIndex={activeIndex}
                isRTL={isRTL}
                webDotSize={webDotSize}
                webDotInactiveStyle={webDotInactiveStyle}
                activeDotColor={activeDotColor}
                onDotPress={onGoToSlide}
            />
            {IS_WEB && showNavigationButtons ? (
                <CarouselWebNavChevrons
                    isRTL={isRTL}
                    activeIndex={activeIndex}
                    onGoToSlide={onGoToSlide}
                />
            ) : null}
            {constructionCompleted !== undefined ? (
                <ConstructionCompletedOverlay constructionCompleted={constructionCompleted} />
            ) : null}
        </>
    );
}

function SingleSlideCarousel({
    slideImages,
    carouselWidth,
    imageTransition,
    constructionCompleted,
    onPress,
    onImagePress,
    onImageError,
}: Readonly<{
    slideImages: ImageSourcePropType[] | null;
    carouselWidth: number;
    imageTransition?: number | undefined;
    constructionCompleted?: number | undefined;
    onPress?: (() => void) | undefined;
    onImagePress: () => void;
    onImageError: (imageKey: string) => void;
}>) {
    const c = useUIColors();
    const singleImage =
        slideImages?.length === 1 ? slideImages[0] : undefined;

    return (
        <View style={[styles.slide, carouselWidth ? { width: carouselWidth } : null]}>
            {singleImage !== undefined ? (
                <NetworkImage
                    url={getNetworkImageUri(singleImage)}
                    style={styles.image}
                    resizeMode="cover"
                    {...(IS_WEB && { onError: () => onImageError(getCarouselImageKey(singleImage, 0)) })}
                    {...(imageTransition !== undefined && { transition: imageTransition })}
                />
            ) : (
                <View style={[styles.imagePlaceholder, { backgroundColor: c.backgroundLevel2 }]}>
                    <PlaceholderImage />
                </View>
            )}
            {/* No grey overlay — avoids white shadow on iOS back-swipe transition */}
            {/* Gradient below dots for better visibility (even for single image) */}
            <LinearGradient
                pointerEvents="none"
                colors={['transparent', 'rgba(0, 0, 0, 0.3)']}
                locations={[0, 1]}
                style={[styles.dotsGradient, IS_WEB && styles.dotsGradientWeb]}
            />
            {constructionCompleted !== undefined ? (
                <ConstructionCompletedOverlay constructionCompleted={constructionCompleted} />
            ) : null}
            {/* Pressable wrapper for single image tap detection */}
            {onPress ? (
                <Pressable
                    style={styles.tapOverlay}
                    onPress={onImagePress}
                />
            ) : null}
        </View>
    );
}

function resolveWebDotSize(useListingPagination: boolean): number {
    if (useListingPagination || (IS_WEB && isSmallScreen())) {
        return WEB_DOT_SIZE_MOBILE;
    }
    return WEB_DOT_SIZE_DESKTOP;
}

function resolveWebDotInactiveStyle(useListingPagination: boolean) {
    if (useListingPagination) {
        return styles.dotWebInactiveListing;
    }
    return styles.dotWebInactive;
}

function resolveSlideImages(
    images: ImageSourcePropType[] | undefined,
): ImageSourcePropType[] | null {
    if (!images || images.length === 0) {
        return null;
    }
    return images;
}

function resolveScrollX(contentOffsetX: number, isRTL: boolean): number {
    if (Platform.OS === 'web' && isRTL) {
        return -contentOffsetX;
    }
    return contentOffsetX;
}

function resolveSlideOffset(
    wrappedIndex: number,
    carouselWidth: number,
    isRTL: boolean,
): number {
    if (Platform.OS === 'web' && isRTL) {
        return -(wrappedIndex * carouselWidth);
    }
    return wrappedIndex * carouselWidth;
}

function useCarouselInteraction(options: {
    isRTL: boolean;
    slideImages: ImageSourcePropType[] | null;
    carouselWidth: number;
    disableAutoScroll?: boolean | undefined;
    autoScrollPaused?: boolean | undefined;
    autoScrollSeed?: string | undefined;
    onScrollStart?: (() => void) | undefined;
    onScrollEnd?: (() => void) | undefined;
    onPress?: (() => void) | undefined;
    onManualIndexChange?: ((index: number) => void) | undefined;
}) {
    const {
        isRTL,
        slideImages,
        carouselWidth,
        disableAutoScroll,
        autoScrollPaused,
        autoScrollSeed,
        onScrollStart,
        onScrollEnd,
        onPress,
        onManualIndexChange,
    } = options;
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [isScrolling, setIsScrolling] = React.useState(false);
    const [canTap, setCanTap] = React.useState(true);
    // React Native setTimeout returns number, not NodeJS.Timeout
    // Using any to avoid type conflicts between React Native and Node.js types
    const scrollTimeoutRef = React.useRef<any>(null);
    const scrollRef = React.useRef<ScrollView>(null);
    const manualGestureRef = React.useRef(false);
    const onManualIndexChangeRef = React.useRef(onManualIndexChange);
    onManualIndexChangeRef.current = onManualIndexChange;
    const activeIndexRef = React.useRef(0);

    const notifyManualIndex = (index: number) => {
        if (!Number.isFinite(index) || index < 0 || index === activeIndexRef.current) {
            return;
        }
        activeIndexRef.current = index;
        onManualIndexChangeRef.current?.(index);
    };

    const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
        const { contentOffset, layoutMeasurement } = e.nativeEvent;
        const width = Math.max(layoutMeasurement.width, 1);
        const scrollX = resolveScrollX(contentOffset.x, isRTL);
        const index = Math.round(scrollX / width);
        if (index !== activeIndex) {
            setActiveIndex(index);
            if (manualGestureRef.current) {
                notifyManualIndex(index);
            }
        }
    };

    const handleScrollBeginDrag = () => {
        manualGestureRef.current = true;
        setIsScrolling(true);
        setCanTap(false);
        onScrollStart?.();
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
    };

    const handleScrollEndDrag = () => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
            manualGestureRef.current = false;
            setIsScrolling(false);
            setTimeout(() => {
                setCanTap(true);
            }, 100);
            onScrollEnd?.();
        }, 200);
    };

    const handleImagePress = () => {
        if (!onPress || isScrolling || !canTap) {
            return;
        }
        onPress();
    };

    const goToSlide = (index: number) => {
        if (!slideImages || carouselWidth <= 0) {
            return;
        }
        const len = slideImages.length;
        const wrapped = ((index % len) + len) % len;
        const offset = resolveSlideOffset(wrapped, carouselWidth, isRTL);
        scrollRef.current?.scrollTo({ x: offset, animated: true });
        notifyManualIndex(wrapped);
        setActiveIndex(wrapped);
    };

    const slideCount = slideImages?.length ?? 0;
    const webAutoScrollMs = !disableAutoScroll ? WEB_CAROUSEL_AUTO_SCROLL_MS : 0;

    React.useEffect(() => {
        activeIndexRef.current = activeIndex;
    }, [activeIndex]);

    React.useEffect(() => {
        // Paused while the surrounding page is actively being scrolled — see
        // the `autoScrollPaused` doc comment on ImageCarouselProps for why.
        if (autoScrollPaused || webAutoScrollMs <= 0 || slideCount < 2 || carouselWidth <= 0) {
            return;
        }

        const { intervalMs, firstDelayMs } = resolveAutoScrollTiming(webAutoScrollMs, autoScrollSeed);
        let timer: ReturnType<typeof setTimeout>;
        const advance = () => {
            const next = (activeIndexRef.current + 1) % slideCount;
            const offset = resolveSlideOffset(next, carouselWidth, isRTL);
            scrollRef.current?.scrollTo({ x: offset, animated: true });
            activeIndexRef.current = next;
            setActiveIndex(next);
            timer = setTimeout(advance, intervalMs);
        };
        timer = setTimeout(advance, firstDelayMs);

        return () => clearTimeout(timer);
    }, [carouselWidth, isRTL, slideCount, webAutoScrollMs, autoScrollPaused, autoScrollSeed]);

    React.useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    return {
        activeIndex,
        scrollRef,
        onScroll,
        handleScrollBeginDrag,
        handleScrollEndDrag,
        handleImagePress,
        goToSlide,
    };
}

// Memoized: screens that render this inside a large single component
// (e.g. PropertyDetailsScreenAdhaMobile, whose many unrelated `useState`
// toggles live alongside the gallery) re-render often for reasons that have
// nothing to do with the images — React.memo skips re-mounting/rebuilding
// the carousel (including its auto-scroll interaction state) when its own
// props haven't actually changed.
export const ImageCarousel = React.memo(function ImageCarousel({ images, height = 230, constructionCompleted, onScrollStart, onScrollEnd, onPress, onManualIndexChange, showNavigationButtons = false, activeDotColor, imageTransition, disableAutoScroll, autoScrollPaused, autoScrollSeed, paginationVariant = 'gallery', loading = false }: Readonly<ImageCarouselProps>) {
    const { isRTL } = useI18nContext();
    const useListingPagination = IS_WEB && paginationVariant === 'listing';
    const webDotSize = resolveWebDotSize(useListingPagination);
    const webDotInactiveStyle = resolveWebDotInactiveStyle(useListingPagination);
    const [carouselWidth, setCarouselWidth] = React.useState(0);
    const [failedImageKeys, setFailedImageKeys] = React.useState<ReadonlySet<string>>(() => new Set());
    const handleImageError = React.useCallback((imageKey: string) => {
        setFailedImageKeys((prev) => (prev.has(imageKey) ? prev : new Set(prev).add(imageKey)));
    }, []);
    const resolvedImages = resolveSlideImages(images);
    // Web: when every image is unusable, show one placeholder (no dots / arrows).
    const allImagesFailed =
        IS_WEB &&
        (resolvedImages?.every(
            (src, i) => !isLoadableImage(src) || failedImageKeys.has(getCarouselImageKey(src, i)),
        ) ?? false);
    const slideImages = allImagesFailed ? null : resolvedImages;
    const showLoadingShimmer = IS_WEB && loading;
    const hasMultipleSlides = slideImages != null && slideImages.length > 1;
    const {
        activeIndex,
        scrollRef,
        onScroll,
        handleScrollBeginDrag,
        handleScrollEndDrag,
        handleImagePress,
        goToSlide,
    } = useCarouselInteraction({
        isRTL,
        slideImages,
        carouselWidth,
        disableAutoScroll,
        autoScrollPaused,
        autoScrollSeed,
        onScrollStart,
        onScrollEnd,
        onPress,
        onManualIndexChange,
    });

    return (
        <View
            style={[styles.wrap, { height }]}
            onLayout={(e) => setCarouselWidth(e.nativeEvent.layout.width)}
        >
            {showLoadingShimmer ? (
                <Skeleton width="100%" height="100%" borderRadius={0} />
            ) : null}
            {!showLoadingShimmer && hasMultipleSlides && slideImages ? (
                <MultiSlideCarousel
                    slideImages={slideImages}
                    carouselWidth={carouselWidth}
                    activeIndex={activeIndex}
                    isRTL={isRTL}
                    webDotSize={webDotSize}
                    webDotInactiveStyle={webDotInactiveStyle}
                    activeDotColor={activeDotColor}
                    imageTransition={imageTransition}
                    showNavigationButtons={showNavigationButtons}
                    constructionCompleted={constructionCompleted}
                    scrollRef={scrollRef}
                    onScroll={onScroll}
                    onScrollBeginDrag={handleScrollBeginDrag}
                    onScrollEndDrag={handleScrollEndDrag}
                    onImagePress={handleImagePress}
                    onGoToSlide={goToSlide}
                    onImageError={handleImageError}
                />
            ) : null}
            {!showLoadingShimmer && !hasMultipleSlides ? (
                <SingleSlideCarousel
                    slideImages={slideImages}
                    carouselWidth={carouselWidth}
                    imageTransition={imageTransition}
                    constructionCompleted={constructionCompleted}
                    onPress={onPress}
                    onImagePress={handleImagePress}
                    onImageError={handleImageError}
                />
            ) : null}
        </View>
    );
});


const styles = StyleSheet.create({
    wrap: {
        position: 'relative',
        borderRadius: 12,
        overflow: 'hidden',
    },
    slide: {
        width: '100%',
        height: '100%',
    },
    image: {
        width: '100%',
        height: '100%',
    },
    imagePlaceholder: {
        width: '100%',
        height: '100%',
        backgroundColor: '#F5F5F5',
    },
    dotsContainer: {
        position: 'absolute',
        left: 0,
        right: 0,
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 6,
        zIndex: 2, // Above gradient
    },
    // Native: keep original bottom inset (do not share with web).
    dotsContainerNative: {
        bottom: 8,
    },
    // Web: lower inset so dots sit near the image edge (Figma gallery).
    dotsContainerWeb: {
        bottom: WEB_DOTS_BOTTOM_OFFSET,
        zIndex: 5,
    },
    dotsContainerRTL: {
        flexDirection: 'row-reverse', // Reverse dot order for RTL
    },
    dotsGradient: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 40, // Gradient height to cover dots area
        zIndex: 1, // Below dots but above images
    },
    dotsGradientWeb: {
        height: 72,
    },
    gradientOverlay: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 80,
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
    },
    gradientBlend: { mixBlendMode: 'multiply' as any },
    // Native pagination (unchanged)
    dot: {
        width: 6,
        height: 6,
        borderRadius: 3,
    },
    // Web-only pagination colors (size set inline: desktop 12 / mobile 8)
    dotWebActive: {
        backgroundColor: '#FFFFFF',
    },
    dotWebInactive: {
        backgroundColor: 'rgba(255, 255, 255, 0.67)',
    },
    /** Match properties-web ListingCard inactive dots. */
    dotWebInactiveListing: {
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
    },
    completionStatusOverlay: {
        position: 'absolute',
        top: 12,
        left: 12,
        backgroundColor: '#e7e7e7',
        paddingHorizontal: 6,
        paddingVertical: 4,
        borderRadius: 8,
    },
    tapOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10, // Above everything except gradients (which have pointerEvents="none")
    },
    chevronButton: {
        position: 'absolute',
        top: '50%',
        marginTop: -20,
        width: 40,
        height: 40,
        borderRadius: 8,
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 3,
    },
    chevronLeft: {
        left: 12,
    },
    chevronRight: {
        right: 12,
    },
});

export default ImageCarousel;
